import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '~/components/button';
import { api } from '~/lib/api';
import { THEMES, useTheme, type ModePref, type ThemeId } from '~/lib/theme';
import { cn } from '~/lib/cn';

export const Route = createFileRoute('/_authed/settings')({
  component: Settings,
});

function Settings() {
  const { data, refetch } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const user = data?.user;
  const [confirmDelete, setConfirmDelete] = useState(false);

  const base = import.meta.env.VITE_API_URL || '';
  const exportUrl = `${base}/api/settings/export`;

  const schedule = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/settings/account/schedule-deletion', {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`${r.status}`);
      return r.json() as Promise<{ scheduledAt: string }>;
    },
    onSuccess: () => refetch(),
  });

  const cancel = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/settings/account/cancel-deletion', {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`${r.status}`);
      return r.json();
    },
    onSuccess: () => refetch(),
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">settings</h1>
      <dl className="mt-6 grid grid-cols-[140px_1fr] gap-y-3 text-sm">
        <dt className="text-muted-foreground">email</dt>
        <dd>{user?.email}</dd>
        <dt className="text-muted-foreground">tier</dt>
        <dd>{user?.tier}</dd>
        <dt className="text-muted-foreground">persona</dt>
        <dd>{user?.personaMode ?? '—'}</dd>
      </dl>

      <ThemePicker />

      <section className="mt-10 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">download your data</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          everything we hold about you, decrypted, as a zip. journal entries, profile history, coaching turns, biometrics, calendar shape, decisions. crisis-routing audit included.
        </p>
        <div className="mt-3">
          <a href={exportUrl} download>
            <Button size="sm" variant="secondary">download export</Button>
          </a>
        </div>
      </section>

      <section className="mt-6 rounded-[var(--radius)] border border-destructive/30 p-4">
        <h2 className="text-sm font-medium">delete account</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          30-day grace. your data is purged after that — vectors, R2 blobs, every row except the safety-routing audit (legal hold).
        </p>
        {schedule.data && (
          <p className="mt-3 text-sm">
            scheduled for <b>{new Date(schedule.data.scheduledAt).toLocaleString()}</b>.{' '}
            <button onClick={() => cancel.mutate()} className="underline">
              cancel
            </button>
          </p>
        )}
        {!schedule.data && (
          <div className="mt-3 flex items-center gap-3">
            {confirmDelete ? (
              <>
                <Button size="sm" variant="destructive" disabled={schedule.isPending} onClick={() => schedule.mutate()}>
                  {schedule.isPending ? 'scheduling…' : 'yes, schedule deletion'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                  back
                </Button>
              </>
            ) : (
              <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(true)}>
                schedule deletion
              </Button>
            )}
          </div>
        )}
      </section>
    </section>
  );
}

function ThemePicker() {
  const { theme, mode, setTheme, setMode } = useTheme();
  const modeOptions: { id: ModePref; label: string }[] = [
    { id: 'system', label: 'system' },
    { id: 'light', label: 'light' },
    { id: 'dark', label: 'dark' },
  ];
  return (
    <section className="mt-10 rounded-[var(--radius)] border border-border p-4">
      <h2 className="text-sm font-medium">appearance</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        pick a palette. each one ships a light and a dark variant.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {THEMES.map((t) => (
          <ThemeSwatch
            key={t.id}
            theme={t.id}
            name={t.name}
            description={t.description}
            swatch={t.swatch}
            selected={theme === t.id}
            onSelect={() => setTheme(t.id)}
          />
        ))}
      </div>

      <div className="mt-5 flex items-center gap-2">
        <span className="text-xs text-muted-foreground">mode</span>
        <div className="inline-flex rounded-[var(--radius-sm)] border border-border bg-muted/40 p-0.5">
          {modeOptions.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              className={cn(
                'px-3 py-1 text-xs rounded-[calc(var(--radius-sm)-2px)] transition-colors',
                mode === m.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              aria-pressed={mode === m.id}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThemeSwatch({
  theme,
  name,
  description,
  swatch,
  selected,
  onSelect,
}: {
  theme: ThemeId;
  name: string;
  description: string;
  swatch: { bg: string; primary: string; accent: string };
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={`use ${name} theme`}
      className={cn(
        'group text-left rounded-[var(--radius)] border p-3 transition-colors',
        selected
          ? 'border-primary ring-2 ring-ring/40'
          : 'border-border hover:border-muted-foreground/40',
      )}
      data-theme-id={theme}
    >
      <div
        className="h-14 w-full rounded-[var(--radius-sm)] border border-border/60 relative overflow-hidden"
        style={{ background: swatch.bg }}
      >
        <span
          className="absolute left-2 bottom-2 h-4 w-4 rounded-full"
          style={{ background: swatch.primary }}
        />
        <span
          className="absolute left-7 bottom-2 h-4 w-4 rounded-full"
          style={{ background: swatch.accent }}
        />
      </div>
      <div className="mt-2 text-sm font-medium">{name}</div>
      <div className="text-[11px] text-muted-foreground leading-snug">{description}</div>
    </button>
  );
}
